/**
 * Cursor-follow grid glow — pointer position + zoom-scaled mask/fade for the
 * glow layer. Grid alignment comes from a duplicate `<Background />` inside
 * the glow layer (same React Flow pattern math as the base grid, lines or dots).
 *
 * Every runtime write lands on `.canvas-cursor-glow-layer` itself (a sibling
 * of the nodes) as inline mask-size / mask-position / opacity. Nothing is ever
 * written on the wrapper: custom properties inherit, so a per-frame write on
 * an ancestor of the nodes dirties style for every node beneath it — WebKit
 * spends hundreds of milliseconds per frame on that alone.
 */

import type { Viewport } from "@xyflow/react";
import { useCallback, useEffect, useRef, type RefObject } from "react";

const BASE_GLOW_RADIUS_PX = 160;
/** Parked far off-screen so the masked circle is fully clipped away. */
const PARKED_PX = -9999;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/** Zoom-scaled glow radius: full size from 50% zoom up, shrinking below. */
export function glowRadiusForZoom(zoom: number) {
  return BASE_GLOW_RADIUS_PX * Math.min(1, zoom / 0.5);
}

/** DS ground behavior: grid fully visible at ≥50% zoom, gone at ≤30%. */
export function gridFadeForZoom(zoom: number) {
  return clamp((zoom - 0.3) / 0.2, 0, 1);
}

type GlowState = {
  /** Wrapper-relative pointer position (unrounded — drives mask-position). */
  tx: number;
  ty: number;
  radius: number;
  fade: number;
  /** Mirrors the wrapper's `:hover` — the glow is hover-only. */
  hovered: boolean;
};

/* The mask is a fixed 2r×2r radial tile (gradient centered, radius
   closest-side) that slides to the cursor via mask-position. Position is the
   tile's top-left, so offset by -r to center the circle on the cursor. */
function writeMask(layer: HTMLElement, s: GlowState) {
  const size = `${2 * s.radius}px ${2 * s.radius}px`;
  const position = `${s.tx - s.radius}px ${s.ty - s.radius}px`;
  layer.style.setProperty("-webkit-mask-size", size);
  layer.style.setProperty("mask-size", size);
  layer.style.setProperty("-webkit-mask-position", position);
  layer.style.setProperty("mask-position", position);
}

function writeOpacity(layer: HTMLElement, s: GlowState, canHover: boolean) {
  layer.style.opacity = canHover && s.hovered ? String(s.fade) : "0";
}

export function useCanvasCursorGlow(
  wrapperRef: RefObject<HTMLDivElement | null>,
  options: { enabled: boolean },
) {
  const { enabled } = options;

  const stateRef = useRef<GlowState>({
    tx: PARKED_PX,
    ty: PARKED_PX,
    radius: BASE_GLOW_RADIUS_PX,
    fade: 1,
    hovered: false,
  });
  // `(hover: hover)` — touch-only devices never show the glow (the old CSS
  // `:hover` rule lived under the same media query).
  const canHoverRef = useRef(false);
  // The layer is a React Flow child rendered after the node tree, so a
  // querySelector from the wrapper walks every node first; cache it and
  // re-query only when the cached element has been unmounted (grid toggle).
  const layerRef = useRef<HTMLElement | null>(null);
  const getLayer = useCallback(() => {
    const cached = layerRef.current;
    if (cached?.isConnected) return cached;
    const el = wrapperRef.current;
    const layer =
      el?.querySelector<HTMLElement>(".canvas-cursor-glow-layer") ?? null;
    layerRef.current = layer;
    return layer;
  }, [wrapperRef]);

  const syncViewport = useCallback(
    (viewport: Viewport) => {
      if (!enabled) return;
      const layer = getLayer();
      if (!layer) return;
      const s = stateRef.current;
      s.radius = glowRadiusForZoom(viewport.zoom);
      s.fade = gridFadeForZoom(viewport.zoom);
      writeMask(layer, s);
      writeOpacity(layer, s, canHoverRef.current);
    },
    [enabled, getLayer],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !enabled) return;

    canHoverRef.current = window.matchMedia("(hover: hover)").matches;
    const s = stateRef.current;
    // The grid may be re-enabled with the pointer already over the wrapper.
    s.hovered = el.matches(":hover");
    const layer = getLayer();
    if (layer) {
      writeMask(layer, s);
      writeOpacity(layer, s, canHoverRef.current);
    }

    // Coalesce pointermove into a single rAF flush — high-poll mice fire many
    // events per frame; we only need the latest position once per frame. The
    // rect is read inside the flush (not cached, not per event): at most one
    // getBoundingClientRect per frame, and always fresh — so the glow stays put
    // even when the wrapper's screen position shifts from sibling layout (e.g.
    // a banner/chat panel above it) without a resize or scroll event.
    let rafId = 0;
    let pendingClientX = 0;
    let pendingClientY = 0;
    let hasPending = false;

    const flush = () => {
      rafId = 0;
      if (!hasPending) return;
      hasPending = false;
      const target = getLayer();
      if (!target) return;
      const rect = el.getBoundingClientRect();
      // Unrounded floats — rounding here would misalign the mask tile.
      s.tx = pendingClientX - rect.left;
      s.ty = pendingClientY - rect.top;
      writeMask(target, s);
    };

    const onPointerEnter = () => {
      s.hovered = true;
      const target = getLayer();
      if (target) writeOpacity(target, s, canHoverRef.current);
    };
    const onPointerMove = (e: PointerEvent) => {
      pendingClientX = e.clientX;
      pendingClientY = e.clientY;
      hasPending = true;
      if (rafId === 0) rafId = requestAnimationFrame(flush);
    };
    const onPointerLeave = () => {
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      hasPending = false;
      s.hovered = false;
      s.tx = PARKED_PX;
      s.ty = PARKED_PX;
      const target = getLayer();
      if (!target) return;
      writeMask(target, s);
      writeOpacity(target, s, canHoverRef.current);
    };

    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerleave", onPointerLeave);
    return () => {
      if (rafId !== 0) cancelAnimationFrame(rafId);
      el.removeEventListener("pointerenter", onPointerEnter);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [wrapperRef, enabled, getLayer]);

  return { syncViewport };
}
